var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { action, observable } from 'mobx';
import Papa from 'papaparse';
import { bulkCreateUsersAction } from '@gtintel/platform-api';
const CSV_HEADERS = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  accountType: 'Account Type',
  role: 'Role',
  partnerRole: 'Partner Role',
  mobilePhone: 'Mobile Phone',
  alternatePhone: 'Alternate Phone',
  company: 'Company',
  streetAddress: 'Street Address',
  apartment: 'Apartment',
  biography: 'Biography',
  tags: 'Tags',
  team: 'Team'
};
const ACCOUNT_TYPES = ['GTI Admin', 'Network Partner', 'Client', 'Dual Account'];
const CLIENT_ROLES = ['Admin', 'Project Manager', 'Analyst'];
const PARTNER_ROLES = ['Network Partner', 'Billing Admin'];
let AdminBulkUserStore = (_class = class AdminBulkUserStore {
  constructor(rootStore) {
    this.rootStore = void 0;
    _initializerDefineProperty(this, "parsedData", _descriptor, this);
    _initializerDefineProperty(this, "validationErrors", _descriptor2, this);
    _initializerDefineProperty(this, "isUploading", _descriptor3, this);
    _initializerDefineProperty(this, "uploadResult", _descriptor4, this);
    _initializerDefineProperty(this, "errorMessage", _descriptor5, this);
    this.rootStore = rootStore;
  }
  async parseCsv(file) {
    return new Promise(resolve => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: results => {
          const data = this.transformCsvData(results.data);
          const errors = this.validateData(data);
          this.parsedData = data;
          this.validationErrors = errors;
          resolve({
            data,
            errors,
            isValid: errors.length === 0
          });
        },
        error: error => {
          this.errorMessage = `CSV parsing error: ${error.message}`;
          resolve({
            data: [],
            errors: [{
              row: 0,
              field: 'file',
              message: `Failed to parse CSV: ${error.message}`
            }],
            isValid: false
          });
        }
      });
    });
  }
  transformCsvData(rawData) {
    return rawData.map(row => ({
      firstName: row[CSV_HEADERS.firstName]?.trim() || '',
      lastName: row[CSV_HEADERS.lastName]?.trim() || '',
      email: row[CSV_HEADERS.email]?.trim() || '',
      accountType: row[CSV_HEADERS.accountType]?.trim() || '',
      role: row[CSV_HEADERS.role]?.trim() || undefined,
      partnerRole: row[CSV_HEADERS.partnerRole]?.trim() || undefined,
      mobilePhone: row[CSV_HEADERS.mobilePhone]?.trim() || undefined,
      alternatePhone: row[CSV_HEADERS.alternatePhone]?.trim() || undefined,
      company: row[CSV_HEADERS.company]?.trim() || undefined,
      streetAddress: row[CSV_HEADERS.streetAddress]?.trim() || undefined,
      apartment: row[CSV_HEADERS.apartment]?.trim() || undefined,
      biography: row[CSV_HEADERS.biography]?.trim() || undefined,
      tags: row[CSV_HEADERS.tags]?.trim() || undefined,
      team: row[CSV_HEADERS.team]?.trim() || undefined
    }));
  }
  validateData(data) {
    const errors = [];
    data.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because index starts at 0 and CSV has header row

      // Required field validations
      if (!row.firstName) {
        errors.push({
          row: rowNumber,
          field: 'firstName',
          message: 'First Name is required',
          value: row.firstName
        });
      }
      if (!row.lastName) {
        errors.push({
          row: rowNumber,
          field: 'lastName',
          message: 'Last Name is required',
          value: row.lastName
        });
      }
      if (!row.email) {
        errors.push({
          row: rowNumber,
          field: 'email',
          message: 'Email is required',
          value: row.email
        });
      } else if (!this.isValidEmail(row.email)) {
        errors.push({
          row: rowNumber,
          field: 'email',
          message: 'Invalid email format',
          value: row.email
        });
      }
      if (!row.accountType) {
        errors.push({
          row: rowNumber,
          field: 'accountType',
          message: 'Account Type is required',
          value: row.accountType
        });
      } else if (!ACCOUNT_TYPES.includes(row.accountType)) {
        errors.push({
          row: rowNumber,
          field: 'accountType',
          message: `Invalid Account Type. Must be one of: ${ACCOUNT_TYPES.join(', ')}`,
          value: row.accountType
        });
      }

      // Role validation for Client and Dual Account
      if (row.accountType === 'Client' || row.accountType === 'Dual Account') {
        if (!row.role) {
          errors.push({
            row: rowNumber,
            field: 'role',
            message: 'Role is required for Client and Dual Account types',
            value: row.role
          });
        } else if (!CLIENT_ROLES.includes(row.role)) {
          errors.push({
            row: rowNumber,
            field: 'role',
            message: `Invalid Role. Must be one of: ${CLIENT_ROLES.join(', ')}`,
            value: row.role
          });
        }
      }

      // Partner Role validation for Network Partner
      if (row.accountType === 'Network Partner') {
        if (row.partnerRole && !PARTNER_ROLES.includes(row.partnerRole)) {
          errors.push({
            row: rowNumber,
            field: 'partnerRole',
            message: `Invalid Partner Role. Must be one of: ${PARTNER_ROLES.join(', ')}`,
            value: row.partnerRole
          });
        }
      }

      // Team validation for Client, Network Partner, and Dual Account
      if (row.accountType === 'Client' || row.accountType === 'Network Partner' || row.accountType === 'Dual Account') {
        if (!row.team) {
          errors.push({
            row: rowNumber,
            field: 'team',
            message: 'Team is required for Client, Network Partner, and Dual Account types',
            value: row.team
          });
        }
      }

      // Email format validation
      if (row.email && !this.isValidEmail(row.email)) {
        errors.push({
          row: rowNumber,
          field: 'email',
          message: 'Invalid email format',
          value: row.email
        });
      }
    });

    // Check for duplicate emails in the CSV
    const emailCounts = new Map();
    data.forEach((row, index) => {
      if (row.email) {
        const rowNumber = index + 2;
        if (!emailCounts.has(row.email)) {
          emailCounts.set(row.email, []);
        }
        emailCounts.get(row.email).push(rowNumber);
      }
    });
    emailCounts.forEach((rows, email) => {
      if (rows.length > 1) {
        rows.forEach(rowNumber => {
          errors.push({
            row: rowNumber,
            field: 'email',
            message: `Duplicate email found in rows: ${rows.join(', ')}`,
            value: email
          });
        });
      }
    });
    return errors;
  }
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
  async submitBulkCreate() {
    if (this.validationErrors.length > 0) {
      return {
        success: false,
        created: 0,
        failed: this.parsedData.length,
        errors: this.validationErrors.map(err => ({
          row: err.row,
          email: '',
          error: `${err.field}: ${err.message}`
        }))
      };
    }
    this.isUploading = true;
    this.errorMessage = '';
    try {
      // Transform data to API format
      const usersToCreate = this.parsedData.map(row => this.transformToApiFormat(row));

      // Call bulk create API
      const result = await bulkCreateUsersAction(usersToCreate);
      this.uploadResult = result;
      this.isUploading = false;
      return result;
    } catch (error) {
      console.error('Bulk user creation error:', error);
      this.isUploading = false;
      this.errorMessage = error?.data?.message || 'Failed to create users';
      const errorResult = {
        success: false,
        created: 0,
        failed: this.parsedData.length,
        errors: [{
          row: 0,
          email: '',
          error: this.errorMessage
        }]
      };
      this.uploadResult = errorResult;
      return errorResult;
    }
  }
  transformToApiFormat(row) {
    // Map role names to enum values
    const roleMap = {
      Admin: 1,
      'Project Manager': 2,
      Analyst: 3,
      'Network Partner': 4,
      'Billing Admin': 5
    };
    return {
      first_name: row.firstName,
      last_name: row.lastName,
      email: row.email,
      is_administrator: row.accountType === 'GTI Admin' || row.accountType === 'Dual Account',
      is_client: row.accountType === 'Client' || row.accountType === 'Dual Account',
      is_network_partner: row.accountType === 'Network Partner',
      role: row.role ? roleMap[row.role] : undefined,
      partner_role: row.partnerRole ? roleMap[row.partnerRole] : undefined,
      phone: row.mobilePhone,
      alter_phone: row.alternatePhone,
      company: row.company,
      street_address: row.streetAddress,
      apartment: row.apartment,
      biography: row.biography,
      team_name: row.team,
      tags: [] // Must be an empty array, not undefined, as serializer iterates over it
      // Tag support could be added in the future if needed
    };
  }
  reset() {
    this.parsedData = [];
    this.validationErrors = [];
    this.isUploading = false;
    this.uploadResult = null;
    this.errorMessage = '';
  }
  downloadTemplate() {
    const headers = Object.values(CSV_HEADERS);
    const exampleRow = ['John', 'Doe', 'john.doe@example.com', 'Client', 'Admin', '', '+1234567890', '', 'Example Corp', '123 Main St', 'Apt 4B', 'Sample biography', '', 'Operations Team'];
    const csv = [headers, exampleRow].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk_user_upload_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "parsedData", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "validationErrors", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "isUploading", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "uploadResult", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "errorMessage", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _applyDecoratedDescriptor(_class.prototype, "parseCsv", [action], Object.getOwnPropertyDescriptor(_class.prototype, "parseCsv"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "transformCsvData", [action], Object.getOwnPropertyDescriptor(_class.prototype, "transformCsvData"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "validateData", [action], Object.getOwnPropertyDescriptor(_class.prototype, "validateData"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isValidEmail", [action], Object.getOwnPropertyDescriptor(_class.prototype, "isValidEmail"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "submitBulkCreate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "submitBulkCreate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "transformToApiFormat", [action], Object.getOwnPropertyDescriptor(_class.prototype, "transformToApiFormat"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "reset", [action], Object.getOwnPropertyDescriptor(_class.prototype, "reset"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "downloadTemplate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "downloadTemplate"), _class.prototype), _class);
export { AdminBulkUserStore as default };